/**
 * ChunkLoadErrorBoundary
 *
 * Catches errors from failed dynamic imports (lazy-loaded chunks)
 * that occur when a new deployment changes chunk hashes while
 * users still have old HTML cached.
 *
 * Shows a friendly "refresh needed" message instead of an ugly error.
 */

import { useRouteError, isRouteErrorResponse } from 'react-router-dom';

/**
 * Detects if an error is a chunk loading failure
 */
function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('failed to fetch dynamically imported module') ||
      message.includes('loading chunk') ||
      message.includes('loading css chunk') ||
      message.includes("expected a javascript module script")
    );
  }
  return false;
}

/**
 * Router error element that shows a refresh prompt for chunk load errors
 */
export function ChunkLoadErrorBoundary() {
  const error = useRouteError();

  // Check if this is a chunk loading error
  if (isChunkLoadError(error)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-magic-50 to-celestial-50">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">✨</div>
          <h1 className="text-2xl font-display font-bold text-neutral-800 mb-2">
            New Version Available
          </h1>
          <p className="text-neutral-600 mb-6">
            We've made some updates! Please refresh the page to get the latest version.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-magic-500 text-white font-semibold rounded-lg hover:bg-magic-600 transition-colors shadow-lg shadow-magic-500/20"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  // For 404 errors, let them through to the normal handling
  if (isRouteErrorResponse(error) && error.status === 404) {
    throw error;
  }

  // For other errors, show a generic error message
  const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50">
      <div className="text-center p-8 max-w-md">
        <div className="text-6xl mb-4">😕</div>
        <h1 className="text-2xl font-display font-bold text-neutral-800 mb-2">
          Something Went Wrong
        </h1>
        <p className="text-neutral-600 mb-4">
          We encountered an unexpected error. Please try refreshing the page.
        </p>
        {import.meta.env.DEV && (
          <pre className="text-left text-xs bg-neutral-100 p-4 rounded-lg overflow-auto mb-4 text-red-600">
            {errorMessage}
          </pre>
        )}
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-neutral-800 text-white font-semibold rounded-lg hover:bg-neutral-700 transition-colors"
        >
          Refresh Page
        </button>
      </div>
    </div>
  );
}
